// =============================================
// Simulation Store — מצב סימולציה גלובלי
// Kipikid — מערכת נוכחות ילדים בגנים
// =============================================
// Store זה מנהל את מצב הסימולציה עבור כל המסכים.
// כל מסך שרוצה להציג שעון סימולציה צריך לקרוא ל-useSimulationStore().
// ה-SimulationBanner מפעיל polling ומעדכן את ה-store.
//
// שינויים:
// - getDisplayTime() מחשב אינטרפולציה בין polls
//   (שעת סימולציה + זמן אמיתי שעבר × ratio)
// - מונע "קפיצות" בשעון בין polls
// - debounce deactivation: דורש 3 polls רצופים עם active=false לפני כיבוי

import { create } from 'zustand';
import { API_BASE_URL } from '../services/api';

interface SimulationState {
  active: boolean;
  currentTime: Date | null;      // Simulation time from last poll
  ratio: number;
  lastPollRealTime: number | null; // Date.now() when last poll arrived
  // Actions
  setStatus: (active: boolean, currentTime: string | null, ratio: number) => void;
  clear: () => void;
  // Helper: get current display time (simulation time if active, else real time)
  // Interpolates between polls for smooth clock display
  getDisplayTime: () => Date;
}

export const useSimulationStore = create<SimulationState>()((set, get) => ({
  active: false,
  currentTime: null,
  ratio: 1,
  lastPollRealTime: null,

  setStatus: (active, currentTimeStr, ratio) => {
    let currentTime: Date | null = null;
    if (currentTimeStr) {
      // Extract time components directly from ISO string
      // Backend sends Israel time labeled as UTC — DO NOT use new Date() directly
      const match = currentTimeStr.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
      if (match) {
        const [, year, month, day, hour, minute, second] = match;
        // Create Date without timezone conversion
        currentTime = new Date(
          parseInt(year), parseInt(month) - 1, parseInt(day),
          parseInt(hour), parseInt(minute), parseInt(second)
        );
      }
    }
    set({
      active,
      currentTime,
      ratio,
      lastPollRealTime: active ? Date.now() : null,
    });
  },

  clear: () => set({ active: false, currentTime: null, ratio: 1, lastPollRealTime: null }),

  getDisplayTime: () => {
    const state = get();
    if (state.active && state.currentTime && state.lastPollRealTime) {
      // Interpolate: simulate continuous time between polls
      // simDisplayTime = lastPolledSimTime + (realTimeSincePoll × ratio)
      const realElapsedMs = Date.now() - state.lastPollRealTime;
      const simElapsedMs = realElapsedMs * state.ratio;
      return new Date(state.currentTime.getTime() + simElapsedMs);
    }
    return new Date();
  },
}));

// =============================================
// Simulation status fetcher (no auth required)
// Uses raw fetch to avoid auth interceptor issues
// Debounce deactivation: requires 3 consecutive inactive polls before deactivating
// =============================================
let pollingInterval: ReturnType<typeof setInterval> | null = null;
let consecutiveInactivePolls = 0;
const DEACTIVATION_THRESHOLD = 3; // require 3 consecutive inactive polls

export function startSimulationPolling(): void {
  if (pollingInterval) return; // Already polling

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/simulation/status`);
      if (!res.ok) return; // Network error — keep current state
      const data = await res.json();
      if (data.success && data.data) {
        const isActive = data.data.active;
        const wasActive = useSimulationStore.getState().active;

        if (isActive) {
          // Simulation is active — update immediately and reset counter
          consecutiveInactivePolls = 0;
          useSimulationStore.getState().setStatus(
            true,
            data.data.currentTime,
            data.data.ratio || 1
          );
        } else if (wasActive) {
          // Was active, now reporting inactive — debounce before deactivating
          consecutiveInactivePolls++;
          if (consecutiveInactivePolls >= DEACTIVATION_THRESHOLD) {
            // Confirmed inactive after multiple polls — deactivate
            useSimulationStore.getState().setStatus(false, null, 1);
            consecutiveInactivePolls = 0;
          }
          // Otherwise keep showing as active (with interpolated time)
        } else {
          // Was already inactive — stay inactive
          consecutiveInactivePolls = 0;
          useSimulationStore.getState().setStatus(false, null, 1);
        }
      }
    } catch {
      // Silent — simulation API may not be available, keep current state
    }
  };

  fetchStatus(); // Immediate first fetch
  pollingInterval = setInterval(fetchStatus, 3000); // Every 3 seconds to recalibrate
}

export function stopSimulationPolling(): void {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
}
